import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Modal, Input, Button, Table, Pagination, Space, message } from 'antd';
import { useEffect, useState } from 'react';
import { useStore } from '../../stores/root-store-context';
import { useTranslation } from 'react-i18next';
import { Skill } from '../../models/Skill';

interface AddSkillProps {
    visible: boolean;
    onCancel: () => void;
    onSkillSelect: (skillId: string, title: string) => void;
    accessToken: string;
    SKILLS_API_URL: string;
    existingSkills: { title: string }[];
}

export const AddSkill: React.FC<AddSkillProps> = ({
    visible,
    onCancel,
    onSkillSelect,
    accessToken,
    SKILLS_API_URL,
    existingSkills,
}) => {
    const [apiSearchInput, setApiSearchInput] = useState('');
    const [apiSkills, setApiSkills] = useState<Skill[]>([]);
    const [currentApiPage, setCurrentApiPage] = useState(1);
    const [pageSize] = useState(7);
    const [loading, setLoading] = useState(false);
    const { applicationStore } = useStore();
    const { t } = useTranslation();

    useEffect(() => {
        fetchSkillsFromAPI();
    }, [visible]);

    const fetchSkillsFromAPI = async (searchQuery = '') => {
        if (!accessToken) return;

        setLoading(true);
        try {
            let url = `${SKILLS_API_URL}?limit=5000`;
            if (searchQuery.trim() !== '') {
                url += `&q=${encodeURIComponent(searchQuery.trim())}`;
            }

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('API request failed');
            }

            const data = await response.json();
            const formattedSkills = data.data
                .map((skill: any) => ({
                    skillId: skill.id,
                    title: skill.name
                }))
                .filter((apiSkill: Skill) =>
                    !existingSkills.some(localSkill =>
                        localSkill.title.toLowerCase() === apiSkill.title.toLowerCase()
                    )
                );

            if(searchQuery.trim().length == 0){
                const shuffledSkills = formattedSkills.sort(() => Math.random() - 0.5);
                const randomSkills = shuffledSkills.slice(0, 500);
    
                setApiSkills(randomSkills);
            }else{
                setApiSkills(formattedSkills);
            }

            setCurrentApiPage(1);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleApiSearch = async () => {
        await fetchSkillsFromAPI(apiSearchInput);
    };

    const handleAddApiSkill = async (skill: Skill) => {
        if (applicationStore.skills.some(s => s.title.toLowerCase() === skill.title.toLowerCase())) {
            message.error(t('error.skillExists'));
            return;
        }

        try {
            setLoading(true);
            const newSkill = await applicationStore.addSkillToStore(skill.title);
            onSkillSelect(newSkill.skillId, newSkill.title);

            setApiSearchInput('');
            setApiSkills([]);
            setCurrentApiPage(1);
            onCancel();
        } catch (error) {
            console.error('Failed to add skill:', error);
        } finally {
            setLoading(false);
        }
    };

    const apiColumns = [
        {
            title: t('table.column.id'),
            dataIndex: 'skillId',
            key: 'skillId',
        },
        {
            title: t('table.column.name'),
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: t('table.column.actions'),
            key: 'actions',
            render: (_: any, record: Skill) => (
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddApiSkill(record)}
                >
                    {t('button.addToLocal')}
                </Button>
            ),
        },
    ];

    return (
        <Modal
            title={t('modal.title')}
            open={visible}
            onCancel={() => {
                onCancel();
                setApiSearchInput('');
                setApiSkills([]);
                setCurrentApiPage(1);
            }}
            footer={null}
            width={800}
        >
            <Space style={{ marginBottom: 16 }}>
                <Input
                    placeholder={t('input.placeholder')}
                    value={apiSearchInput}
                    onChange={(e) => setApiSearchInput(e.target.value)}
                    style={{ width: 300 }}
                    onPressEnter={handleApiSearch}
                />
                <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleApiSearch}
                    loading={loading}
                >
                    {t('button.search')}
                </Button>
            </Space>
            <Table
                columns={apiColumns}
                dataSource={apiSkills.slice((currentApiPage - 1) * pageSize, currentApiPage * pageSize)}
                rowKey="skillId"
                loading={loading}
                pagination={false}
            />
            <Pagination
                current={currentApiPage}
                pageSize={pageSize}
                total={apiSkills.length}
                onChange={(page) => setCurrentApiPage(page)}
                showSizeChanger={false}
                style={{ marginTop: 16, textAlign: 'center' }}
            />
        </Modal>
    );
};